/**
 * Snake Snacks cart helpers (loaded on every page).
 *
 * - <snake-add>: the "+" button on product cards; turns into a − 1 + stepper once added.
 * - Keeps every stepper and every [data-snake-cart-count] badge in sync with the cart.
 * - Publishes Shopify's standard cart event so Horizon's cart drawer and cart icon update too.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { sectionRenderer, morphSection } from '@theme/section-renderer';
import { resetShimmer } from '@theme/utilities';
/** UI words in the shopper's language (window.SnakeI18n is printed by snippets/snake-head.liquid). */
const T = (key, fallback) => (window.SnakeI18n && window.SnakeI18n[key]) || fallback;

const routes = window.Theme?.routes ?? {};
const CART_URL = routes.cart_url || '/cart';
const UPDATE_URL = routes.cart_update_url || '/cart/update';

/** @type {Map<string, {quantity: number, key: string}>} variant id -> line */
let lines = new Map();
let itemCount = 0;
let seeded = false;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Re-triggers a CSS animation class (snake-motion.css). */
function restartAnimation(el, className) {
  if (reduceMotion.matches) return;
  el.classList.remove(className);
  void el.offsetWidth;
  el.classList.add(className);
  el.addEventListener('animationend', () => el.classList.remove(className), { once: true });
}

/** The product photo arcs into the cart icon, which then gives a little shake. */
function flyToCart(source) {
  if (reduceMotion.matches) return;
  const inView = (/** @type {DOMRect} */ r) => r.width > 40 && r.bottom > 0 && r.top < window.innerHeight;
  /** @type {HTMLImageElement | null} */
  let photo = /** @type {HTMLImageElement | null} */ (source.closest('.snc')?.querySelector('.snc__img'));
  /** @type {DOMRect | null} where the flight starts */
  let from = photo ? photo.getBoundingClientRect() : null;
  if (!photo && source.closest('[data-snk-pdp]')) {
    // product page: the biggest gallery photo on screen (the gallery also holds hidden copies)
    const photos = /** @type {HTMLImageElement[]} */ (Array.from(document.querySelectorAll('.snk-pdp__media .product-media__image')));
    const visible = photos
      .map((img) => ({ img, r: img.getBoundingClientRect() }))
      .filter(({ r }) => inView(r))
      .sort((a, b) => b.r.width * b.r.height - a.r.width * a.r.height)[0];
    photo = visible?.img || photos.find((img) => img.currentSrc) || null;
    // photo scrolled away (e.g. the sticky add bar): start from the button that was tapped
    from = visible ? visible.r : source.getBoundingClientRect();
  }
  const target = /** @type {HTMLElement | undefined} */ (
    Array.from(document.querySelectorAll('.snk-hd__cart .snk-icon')).find((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.top >= 0 && r.bottom <= window.innerHeight;
    })
  );
  if (!photo || !from || !target || !photo.currentSrc) return;

  const a = from;
  const b = target.getBoundingClientRect();
  const size = 56;
  const x0 = a.left + a.width / 2 - size / 2;
  const y0 = a.top + a.height / 2 - size / 2;
  const dx = b.left + b.width / 2 - size / 2 - x0;
  const dy = b.top + b.height / 2 - size / 2 - y0;

  const fly = document.createElement('img');
  fly.className = 'snk-fly';
  fly.alt = '';
  fly.src = photo.currentSrc;
  fly.style.left = `${x0}px`;
  fly.style.top = `${y0}px`;
  document.body.append(fly);
  fly
    .animate(
      [
        { transform: 'translate(0, 0) scale(1.15) rotate(0deg)', opacity: 1 },
        { transform: `translate(${dx * 0.5}px, ${Math.min(0, dy) - 70}px) scale(0.85) rotate(-12deg)`, opacity: 1, offset: 0.45 },
        { transform: `translate(${dx}px, ${dy}px) scale(0.2) rotate(8deg)`, opacity: 0.3 },
      ],
      { duration: 650, easing: 'cubic-bezier(.45, 0, .55, 1)' }
    )
    .finished.then(
      () => {
        fly.remove();
        restartAnimation(target, 'snk-cart-shake');
      },
      () => fly.remove()
    );
}
/*
  ─────────────────────────────── How the cart stays reliable ───────────────────────────────
  1. One write at a time. Quick taps (even on different products) are bundled into a single
     /cart/update.js call, and nothing is sent while the cart drawer is saving a change of its
     own (and vice versa). Shopify's cart can't take overlapping writes: they overwrite each
     other and silently drop items.
  2. Newest data wins. Every cart snapshot gets a version number; an older response can never
     overwrite a newer one.
  3. Few requests. The drawer is only re-fetched when what it shows differs from the real cart,
     or after an error.
  4. Back off when Shopify says "too many requests" (429): pause, keep the shopper's taps, retry.
  5. Self-heal. After any error the drawer is re-rendered from the real cart so it can't freeze.
*/
const RATE_LIMIT_PAUSE = 20000;
const CART_ERROR_EVENT = StandardEvents.cartError || 'shopify:cart:error';

/** @type {Map<string, {qty: number, source: HTMLElement}>} taps waiting to be sent */
const pending = new Map();
/** @type {Map<string, number>} quantities in the request currently being saved */
const sending = new Map();
let flushTimer = 0;
let saving = false;
/** cart changes started by the theme itself (drawer +/−, product forms) that are still saving */
let externalWrites = 0;
let coolDownUntil = 0;
let rateLimitRetries = 0;
let lastCartFetchAt = 0;
let cartVersion = 0;

const now = () => Date.now();
const coolingDown = () => now() < coolDownUntil;
const busy = () => saving || pending.size > 0 || externalWrites > 0;

class CartRequestError extends Error {
  /** @param {string} message @param {{status?: number, rateLimited?: boolean}} [info] */
  constructor(message, info = {}) {
    super(message);
    this.status = info.status;
    this.rateLimited = Boolean(info.rateLimited);
  }
}

/** Fetches a cart endpoint and returns its JSON; anything unexpected becomes a CartRequestError. */
async function fetchCartJSON(url, options = {}) {
  const res = await fetch(url, { ...options, headers: { Accept: 'application/json', ...(options.headers || {}) } });
  if (res.status === 429) {
    coolDownUntil = Math.max(coolDownUntil, now() + RATE_LIMIT_PAUSE);
    throw new CartRequestError('Too many requests', { status: 429, rateLimited: true });
  }
  // Judge the body, not the Content-Type header: Shopify's live cart endpoints send their JSON
  // as "text/javascript", while an error/bot-check page is HTML and won't parse.
  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    throw new CartRequestError(`Unexpected response (${res.status})`, { status: res.status });
  }
  const failed = !res.ok || (typeof data?.status === 'number' && data.status >= 400);
  if (failed) throw new CartRequestError(data?.description || data?.message || 'Cart update failed', { status: res.status });
  return data;
}

function applyCart(cart) {
  cartVersion++;
  lines = new Map();
  for (const item of cart.items ?? []) {
    const id = String(item.variant_id);
    const prev = lines.get(id);
    lines.set(id, { quantity: (prev?.quantity ?? 0) + item.quantity, key: prev?.key ?? item.key });
  }
  const previous = itemCount;
  itemCount = cart.item_count ?? (cart.items ?? []).reduce((n, i) => n + i.quantity, 0);
  renderAll();
  document.querySelectorAll('[data-snake-cart-count]').forEach((el) => {
    el.textContent = String(itemCount);
    el.toggleAttribute('hidden', itemCount === 0);
    if (itemCount > previous && seeded) restartAnimation(el, 'is-pop');
  });
  seeded = true;
}

/** Loads the real cart now (unless something is saving or we're backing off). */
async function fetchCartNow() {
  if (busy() || coolingDown()) return;
  const startedAt = cartVersion;
  try {
    const cart = await fetchCartJSON(`${CART_URL}.js`);
    lastCartFetchAt = now();
    if (startedAt !== cartVersion || busy()) return;
    applyCart(cart);
  } catch (e) {
    if (!(e instanceof CartRequestError && e.rateLimited)) console.warn('[snake-cart] refresh failed', e);
  }
}

let refreshTimer = 0;
function refreshCart(delay = 150) {
  clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(() => {
    if (busy()) {
      refreshCart(400);
      return;
    }
    fetchCartNow();
  }, delay);
}

/**
 * Sends every pending quantity in one request.
 * @param {Map<string, {qty: number, source: HTMLElement}>} batch
 * @returns {Promise<'ok' | 'rate-limited' | 'error'>}
 */
async function sendBatch(batch) {
  /** @type {Record<string, number>} */
  const updates = {};
  /** @type {{id: string, qty: number, prev: number, deferred: any}[]} */
  const changes = [];

  batch.forEach(({ qty, source }, id) => {
    const current = lines.get(id);
    const prev = current?.quantity ?? 0;
    if (qty === prev) return;
    updates[id] = qty;
    const deferred = CartLinesUpdateEvent.createPromise();
    source.dispatchEvent(
      qty > prev
        ? new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: id, quantity: qty - prev }],
            promise: deferred.promise,
          })
        : new CartLinesUpdateEvent({
            action: qty === 0 ? 'remove' : 'update',
            context: 'product',
            lines: [{ id: current?.key ?? id, quantity: qty }],
            promise: deferred.promise,
          })
    );
    changes.push({ id, qty, prev, deferred });
  });
  if (!changes.length) return 'ok';

  const resolveAll = (cart, didError) =>
    changes.forEach(({ deferred }) =>
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: didError ? undefined : cart.sections ?? undefined,
          source: 'snake-add',
          didError,
        },
      })
    );

  // The response also carries the drawer's new HTML, so it updates instantly.
  const sectionId = drawerSectionId();
  try {
    const cart = await fetchCartJSON(`${UPDATE_URL}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates,
        ...(sectionId ? { sections: sectionId, sections_url: window.location.pathname } : {}),
      }),
    });
    lastCartFetchAt = now();
    applyCart(cart);
    resolveAll(cart, false);

    const short = changes.find(({ id, qty }) => (lines.get(id)?.quantity ?? 0) < qty);
    if (short) toast(T('only_n', 'Only {n} available').replace('{n}', lines.get(short.id)?.quantity ?? 0));
    else if (changes.some(({ prev, qty }) => prev === 0 && qty > 0)) {
      toast(T('added', 'Added to cart'), { label: T('view_cart', 'View cart'), onClick: openCartDrawer, snake: true });
    }
    return 'ok';
  } catch (error) {
    const rateLimited = error instanceof CartRequestError && error.rateLimited;
    // Report the failure. (Resolving would make the drawer re-fetch itself — an extra request
    // exactly when Shopify is asking us to slow down. We repair it ourselves below.)
    changes.forEach(({ deferred }) => deferred.reject(error));
    if (rateLimited) return 'rate-limited';
    console.warn('[snake-cart]', error);
    toast(T('cart_error', "Couldn't update your cart — please try again"));
    // put the real cart back on screen once this save is fully finished
    refreshCart(200);
    scheduleDrawerSync(400, true);
    return 'error';
  }
}

function scheduleFlush(delay) {
  clearTimeout(flushTimer);
  flushTimer = window.setTimeout(flush, Math.max(50, delay));
}

async function flush() {
  flushTimer = 0;
  if (!pending.size) return;
  // wait our turn: our own save, a save started from the drawer, or a rate-limit pause
  if (saving || externalWrites > 0) return scheduleFlush(300);
  if (coolingDown()) return scheduleFlush(coolDownUntil - now() + 100);

  const batch = new Map(pending);
  pending.clear();
  batch.forEach(({ qty }, id) => sending.set(id, qty));
  saving = true;
  setDrawerLocked(true);
  let result = 'ok';
  try {
    result = await sendBatch(batch);
  } finally {
    saving = false;
    sending.clear();
    setDrawerLocked(false);
    if (result === 'rate-limited') {
      // keep what the shopper tapped (unless they've changed it since) and try again after the pause
      batch.forEach((entry, id) => pending.has(id) || pending.set(id, entry));
      rateLimitRetries++;
      if (rateLimitRetries <= 3) {
        toast(T('busy', 'Busy moment — finishing your cart update in a few seconds…'));
        scheduleFlush(coolDownUntil - now() + 100);
      } else {
        rateLimitRetries = 0;
        pending.clear();
        toast(T('cart_error', "We couldn't update your cart right now — please try again in a minute"));
      }
    } else {
      rateLimitRetries = 0;
    }
    renderAll();
    if (pending.size && !flushTimer) scheduleFlush(50);
    else if (!pending.size) scheduleDrawerSync();
  }
}

/** Debounces rapid taps into one request. */
function queueQuantity(variantId, quantity, source) {
  pending.set(variantId, { qty: quantity, source });
  document.querySelectorAll(`snake-add[data-variant-id="${variantId}"]`).forEach((el) => /** @type {any} */ (el).render?.());
  scheduleFlush(350);
}

function renderAll() {
  document.querySelectorAll('snake-add').forEach((el) => /** @type {any} */ (el).render?.());
}

/** Shown quantity: what the shopper just tapped, then what is being saved, then the real cart. */
function displayQuantity(variantId) {
  return pending.get(variantId)?.qty ?? sending.get(variantId) ?? lines.get(variantId)?.quantity ?? 0;
}

/* ───────── cart drawer: always matches the real cart ───────── */

function drawerItems() {
  return /** @type {HTMLElement | null} */ (document.querySelector('#cart-drawer cart-items-component'));
}

function drawerSectionId() {
  return drawerItems()?.dataset.sectionId || '';
}

/** While our own save is in flight the drawer's buttons wait, so the two can't write at once. */
function setDrawerLocked(locked) {
  document.documentElement.toggleAttribute('data-snake-cart-saving', locked);
}
document.addEventListener(
  'click',
  (event) => {
    if (!saving) return;
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.closest?.('cart-items-component button, cart-items-component input')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);

/** True when every line the drawer shows matches the real cart (no request needed). */
function drawerMatchesCart() {
  const items = drawerItems();
  if (!items) return true;
  /** @type {Map<string, number>} */
  const shown = new Map();
  items.querySelectorAll('[data-key]').forEach((row) => {
    const input = /** @type {HTMLInputElement | null} */ (row.querySelector('input[ref="quantityInput"], input[type="number"]'));
    if (input) shown.set(/** @type {HTMLElement} */ (row).dataset.key || '', Number(input.value));
  });
  if (shown.size !== lines.size) return false;
  for (const { key, quantity } of lines.values()) {
    if (shown.get(key) !== quantity) return false;
  }
  return true;
}

let drawerSyncTimer = 0;
let drawerSyncSeq = 0;
let drawerSyncForce = false;
let drawerSyncRetries = 0;
function scheduleDrawerSync(delay = 250, force = false) {
  drawerSyncForce = drawerSyncForce || force;
  clearTimeout(drawerSyncTimer);
  drawerSyncTimer = window.setTimeout(syncDrawer, delay);
}

/** Re-renders the drawer from a fresh, never-cached copy of the cart, when it needs it. */
async function syncDrawer() {
  if (busy()) return scheduleDrawerSync(300);
  const force = drawerSyncForce;
  if (!force && drawerMatchesCart()) return;
  if (coolingDown()) {
    if (drawerSyncRetries++ < 2) scheduleDrawerSync(coolDownUntil - now() + 100, force);
    return;
  }
  const sectionId = drawerSectionId();
  if (!sectionId) return;
  drawerSyncForce = false;
  const seq = ++drawerSyncSeq;
  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set('section_id', sectionId);
  url.searchParams.set('_', String(now()));
  try {
    const res = await fetch(url.toString(), { cache: 'no-store', headers: { Accept: 'text/html' } });
    if (res.status === 429) {
      coolDownUntil = Math.max(coolDownUntil, now() + RATE_LIMIT_PAUSE);
      if (drawerSyncRetries++ < 2) scheduleDrawerSync(RATE_LIMIT_PAUSE + 100, true);
      return;
    }
    if (!res.ok) throw new Error(`Drawer render failed (${res.status})`);
    const html = await res.text();
    // a newer sync started, or the cart is changing again: skip this one
    if (seq !== drawerSyncSeq || busy()) return;
    drawerSyncRetries = 0;
    const items = drawerItems();
    sectionRenderer.abortRender(sectionId);
    await morphSection(sectionId, html, {
      mode: 'hydration',
      // empty → non-empty brings in the summary markup and its stylesheet
      injectStylesheet: Boolean(items?.querySelector('[data-cart-drawer-empty]')),
    });
    // never leave the drawer greyed out / disabled after a repair
    const fresh = drawerItems();
    if (fresh) {
      fresh.classList.remove('cart-items-disabled');
      resetShimmer(fresh);
    }
  } catch (e) {
    console.warn('[snake-cart] drawer sync failed', e);
  }
}

export function openCartDrawer() {
  const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
  if (drawer && typeof drawer.open === 'function') drawer.open();
  else window.location.href = CART_URL;
}

/* ───────── toast ───────── */

let toastEl = null;
let toastTimer = 0;
/**
 * @param {string} message
 * @param {{label: string, onClick: () => void, snake?: boolean}} [action]
 */
export function toast(message, action) {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'snk-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    document.body.append(toastEl);
  }
  toastEl.replaceChildren(Object.assign(document.createElement('span'), { textContent: message }));
  if (action?.snake) {
    const snake = Object.assign(document.createElement('img'), {
      className: 'snk-toast__snake',
      src: 'https://cdn.shopify.com/s/files/1/0735/9047/2855/files/flexoriginal.png?v=1787794653',
      alt: '',
    });
    toastEl.prepend(snake);
  }
  if (action) {
    const btn = Object.assign(document.createElement('button'), {
      type: 'button',
      className: 'snk-toast__action',
      textContent: action.label,
    });
    btn.addEventListener('click', () => {
      toastEl.classList.remove('is-on');
      action.onClick();
    });
    toastEl.append(btn);
  }
  requestAnimationFrame(() => toastEl.classList.add('is-on'));
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toastEl.classList.remove('is-on'), 2600);
}

/* ───────── <snake-add> ───────── */

class SnakeAdd extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  get variantId() {
    return this.dataset.variantId ?? '';
  }

  get max() {
    const n = Number(this.dataset.max);
    return Number.isFinite(n) && n > 0 ? n : 99;
  }

  get quantity() {
    return displayQuantity(this.variantId);
  }

  render() {
    const plus = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-add]'));
    const stepper = /** @type {HTMLElement | null} */ (this.querySelector('.snk-add__stepper'));
    if (!plus || !stepper) return;
    const q = this.quantity;
    plus.hidden = q > 0;
    stepper.hidden = q === 0;
    const qty = /** @type {HTMLElement | null} */ (this.querySelector('.snk-add__qty'));
    if (qty && qty.textContent !== String(q)) {
      qty.textContent = String(q);
      if (q > 0) restartAnimation(qty, 'is-roll');
    }
    const inc = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-inc]'));
    if (inc) inc.disabled = q >= this.max;
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('button');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    event.stopPropagation();

    const q = this.quantity;
    let next = q;
    if (button.matches('[data-add], [data-inc]')) next = Math.min(q + 1, this.max);
    if (button.matches('[data-dec]')) next = Math.max(q - 1, 0);
    if (next === q) return;

    queueQuantity(this.variantId, next, this);
    if (next > q) flyToCart(this);
    if (q === 0) {
      // keep focus inside the control after the + turns into the stepper
      requestAnimationFrame(() => /** @type {HTMLElement | null} */ (this.querySelector('[data-inc]'))?.focus());
    }
  };
}

if (!customElements.get('snake-add')) customElements.define('snake-add', SnakeAdd);

/* ───────── keep in sync with changes made elsewhere (drawer, product page) ───────── */

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const source = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement ? event.target : null);
  if (source?.closest('snake-add')) return;
  const promise = /** @type {any} */ (event).promise;
  if (!promise) return;
  // a change started by the theme itself: our own saves wait until it's done
  externalWrites++;
  let finished = false;
  const done = () => {
    if (finished) return;
    finished = true;
    externalWrites = Math.max(0, externalWrites - 1);
    if (pending.size && !flushTimer) scheduleFlush(50);
  };
  // safety net: never wait forever on a change that doesn't report back
  window.setTimeout(done, 15000);
  promise.then(
    (result) => {
      done();
      const detail = result?.detail;
      // the response already contains the whole cart: use it instead of fetching again
      if (detail?.items && !detail.didError) {
        lastCartFetchAt = now();
        applyCart({ items: detail.items, item_count: detail.itemCount });
      } else {
        refreshCart();
        scheduleDrawerSync(600, true);
      }
    },
    () => {
      done();
      refreshCart();
      scheduleDrawerSync(600, true);
    }
  );
});

// any cart error (e.g. a change that points at a line that no longer exists): repair the drawer
document.addEventListener(CART_ERROR_EVENT, () => {
  refreshCart(300);
  scheduleDrawerSync(600, true);
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshCart();
});

// When the drawer opens: make sure it shows the real cart (fetches only if something differs).
document.addEventListener('theme-drawer:open', async (event) => {
  const drawer = /** @type {HTMLElement} */ (event.target);
  if (drawer?.id !== 'cart-drawer') return;
  // the cart may have changed in another tab; re-check at most once a minute
  if (now() - lastCartFetchAt > 60000) await fetchCartNow();
  scheduleDrawerSync(0);
});

document.addEventListener('click', (event) => {
  const trigger = /** @type {HTMLElement} */ (event.target).closest('[data-snake-open-cart]');
  if (!trigger) return;
  event.preventDefault();
  openCartDrawer();
});

window.SnakeCart = { open: openCartDrawer, refresh: refreshCart, toast };

// snippets/snake-head.liquid seeds the cart so steppers show the right number on first paint
if (window.SnakeCartSeed) applyCart(window.SnakeCartSeed);
else refreshCart();
